<template>
    <div class="ws-step">
        <div v-if="stepTitle || stepDescription" class="ws-step-header">
            <h2 class="ws-step-title">{{ stepTitle }}</h2>
            <p v-if="stepDescription" class="ws-step-desc">{{ stepDescription }}</p>
        </div>

        <div class="ws-step-fields">
            <div
                v-for="attr in visibleAttributes"
                :key="attr.id"
                class="ws-field-wrap"
                :class="{ 'has-error': fieldErrors[attr.id] }"
            >
                <component
                    :is="getFieldComponent(attr)"
                    :attribute="attr"
                    :value="answers[attr.id]"
                    :locale="locale"
                    :error="fieldErrors[attr.id]"
                    :is-backoffice="isBackoffice"
                    @input="onFieldInput(attr.id, $event)"
                />
                <span v-if="fieldErrors[attr.id]" class="ws-field-error">
                    {{ fieldErrors[attr.id] }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FIELD_REGISTRY from './fieldRegistry'

// Import all field components
import TextField from './Fields/TextField.vue'
import TextareaField from './Fields/TextareaField.vue'
import RichTextField from './Fields/RichTextField.vue'
import SelectField from './Fields/SelectField.vue'
import RadioField from './Fields/RadioField.vue'
import CheckboxField from './Fields/CheckboxField.vue'
import DateField from './Fields/DateField.vue'
import LocationField from './Fields/LocationField.vue'
import GeolocationAutomaticField from './Fields/GeolocationAutomaticField.vue'
import CategoryField from './Fields/CategoryField.vue'
import FileUploadField from './Fields/FileUploadField.vue'
import RatingField from './Fields/RatingField.vue'
import GdprField from './Fields/GdprField.vue'
import AudioField from './Fields/AudioField.vue'
import HiddenField from './Fields/HiddenField.vue'

const COMPONENTS = {
    TextField,
    TextareaField,
    RichTextField,
    SelectField,
    RadioField,
    CheckboxField,
    DateField,
    LocationField,
    GeolocationAutomaticField,
    CategoryField,
    FileUploadField,
    RatingField,
    GdprField,
    AudioField,
    HiddenField
}

export default {
    name: 'WizardStep',
    components: COMPONENTS,
    props: {
        step: { type: Object, required: true },
        stepIndex: { type: Number, required: true },
        answers: { type: Object, required: true },
        locale: { type: String, default: 'fr' },
        // In backoffice, fields hidden for the citizen are still rendered so the
        // operator can fill them; in the public flow they stay hidden.
        isBackoffice: { type: Boolean, default: false }
    },
    data() {
        return {
            fieldErrors: {}
        }
    },
    computed: {
        stepTitle() {
            if (this.step.name) return this.step.name
            if (this.step.translations) {
                const t = this.step.translations.find((tr) => tr.locale === this.locale)
                if (t && t.name) return t.name
            }
            return ''
        },
        stepDescription() {
            if (this.step.content) return this.step.content
            if (this.step.translations) {
                const t = this.step.translations.find((tr) => tr.locale === this.locale)
                if (t && t.content) return t.content
            }
            return ''
        },
        visibleAttributes() {
            return (this.step.attributes || []).filter((attr) => {
                // The "Champ caché" type (2) and the "Caché (citoyen)" flag both
                // hide a field from the public flow, but the backoffice operator
                // must see and fill every field.
                if (this.isBackoffice) return true
                return attr.form_attribute_type_id !== 2 && attr.hidden !== 1
            })
        }
    },
    methods: {
        getFieldComponent(attr) {
            // A "Champ caché" (type 2) renders as a non-visible input in the public
            // flow; in the backoffice the operator must actually fill it, so give it
            // a real text input instead of the hidden one.
            if (attr.form_attribute_type_id === 2 && this.isBackoffice) return 'TextField'
            const entry = FIELD_REGISTRY[attr.form_attribute_type_id]
            if (!entry) return 'TextField'
            return entry.component
        },
        onFieldInput(attrId, value) {
            this.$emit('update-answer', { attributeId: attrId, value })
            if (this.fieldErrors[attrId]) {
                this.$delete(this.fieldErrors, attrId)
            }
        },
        validate() {
            this.fieldErrors = {}
            let valid = true

            ;(this.step.attributes || []).forEach((attr) => {
                // Fields not rendered must never block submission client-side.
                // In the backoffice every field is rendered (incl. "Champ caché"
                // and "Caché (citoyen)"), so only skip validation in the public flow.
                if (!this.isBackoffice && (attr.form_attribute_type_id === 2 || attr.hidden === 1)) return

                const isRequired = attr.requiredPublic || attr.requiredForWizard
                    || (this.isBackoffice && attr.requiredBackoffice)
                if (!isRequired) return

                const val = this.answers[attr.id]
                if (val === null || val === undefined || val === '' || val === false
                    || (Array.isArray(val) && val.length === 0)) {
                    this.$set(this.fieldErrors, attr.id, this.$t('form_builder.validation.required'))
                    valid = false
                }
            })

            return valid
        },
        getAttrLabel(attr) {
            if (attr.translations) {
                const t = attr.translations.find((tr) => tr.locale === this.locale)
                if (t && t.label) return t.label
            }
            return attr.label || ''
        }
    }
}
</script>

<style scoped>
.ws-step-header {
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #f1f5f9;
}

.ws-step-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
}

.ws-step-desc {
    font-size: 0.88rem;
    color: #64748b;
    margin: 8px 0 0;
    line-height: 1.6;
}

.ws-step-fields {
    display: flex;
    flex-direction: column;
    gap: 28px;
}

.ws-field-wrap {
    padding: 20px 24px;
    background: #fafbfc;
    border: 1.5px solid #f1f5f9;
    border-radius: 14px;
    transition: all 0.2s ease;
}

.ws-field-wrap:hover {
    border-color: #e2e8f0;
    background: #f8fafc;
}

.ws-field-wrap:focus-within {
    border-color: var(--theme-primary, #3b82f6) !important;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06) !important;
}

.ws-field-wrap.has-error {
    border-color: #fca5a5;
    background: #fef2f2;
    animation: ws-shake 0.3s ease;
}

@keyframes ws-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
}

.ws-field-error {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    color: #ef4444;
    margin-top: 8px;
    font-weight: 500;
}
</style>
